import React, {useEffect, useState} from 'react';
import axios from 'axios';
import * as Sentry from "@sentry/react";
import * as api from "./services/api";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";

import {useGlobalState} from "./GlobalState";
import {LoadingOverlay} from "./components/LoadingOverlay";
import {ApiError} from "./models/ApiError";


const AuthenticationContext = React.createContext();

const AuthenticationProvider = props => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isInitializing, setIsInitializing] = useState(true);
    const {loadUserData, resetGlobalState} = useGlobalState();
    const queryClient = useQueryClient();

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const initialize = async () => {
            /*
             * Initialize Axios
             */
            // common headers
            axios.defaults.headers.common['Content-Type'] = 'application/json';

            // allow axios to send and set cookie
            axios.defaults.withCredentials = true;

            // Add baseURL for each api - only in production
            if (process.env.NODE_ENV === "production") {
                axios.defaults.baseURL = 'https://www.livignoitaly.it';
            }

            // handle 401( unauthorized error) and redirect to login Page
            axios.interceptors.response.use(
                (response) => response,
                (error) => {
                    let apiError = new ApiError();

                    if (error.response) {
                        // The request was made and the server responded with a status code
                        // that falls out of the range of 2xx
                        const {status, data} = error.response;

                        if (status === 401) {
                            setIsAuthenticated(false);
                            resetGlobalState();

                            // Clear all the cached data to prevent the next logged-in user
                            // to see data of the previous one (the data indeed might be in
                            // the stale status)
                            queryClient.clear()
                        }

                        if (data && typeof data === 'object' && data.hasOwnProperty('error')) {
                            apiError.title = data.error;
                            apiError.descriptionsArray = data.messages;
                        } else {
                            Sentry.captureMessage(`Got an unknown response from the server: ${data}`);
                            console.error("Got an unknown response from the server:", data)
                        }

                        // Add http status to api error
                        apiError.httpStatus = status;
                    }

                    return Promise.reject(apiError);
                });


            // Check whether the user is already authenticated
            await checkAuthentication();
            setIsInitializing(false);
        };
        initialize();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Try to load the userData if succeed then the user is authenticated otherwise
    // the user is not authenticated. This function is called on the first render of the
    // Authentication context to check whether the user is already authenticated and
    // from the login page to load the user data.
    const checkAuthentication = async () => {
        try {
            const userData = await loadUserData();
            setIsAuthenticated(true);
            const {name, surname, email} = userData.user;
            Sentry.setUser({username: `${surname} ${name}`, email: email});
            return true;
        } catch (e) {
            setIsAuthenticated(false);
            resetGlobalState();
            return false;
        }
    };

    const goToPrivateArea = () => {
        // Get the original from path before the user were redirected the first time.
        // If the from field is not set in the location state then it means that the user has not been
        // redirected but reached the login page directly. In such case go to /
        const from = location.state?.from ? location.state.from.pathname + location.state.from.search : '/'

        // Replace the current path with the new path so that the login path is no more in the history
        // and if the user press back it will be in the page before the login.
        navigate(from, { replace: true });
    }

    const login = async (username, password, rememberMe = false) => {
        await api.Authentication.login(username, password, rememberMe);
        const loginSucceed = await checkAuthentication();

        if (loginSucceed === true) {
            goToPrivateArea();
        }else {
            throw new Error("Impossibile caricare i dati dell'utente");
        }
    };

    const logout = async () => {
        await api.Authentication.logout();

        // First navigate to the main page so that if the user change account
        // in the next login he/she will be redirected to the main page
        // (avoid an error iw the user was is a page like quotes/12345 a
        // and the next user is not allowed to see that quote)
        navigate('/');

        setIsAuthenticated(false);
        resetGlobalState();

        // Clear all the cached data to prevent the next logged-in user
        // to see data of the previous one (the data indeed might be in
        // the stale status)
        queryClient.clear()
    };

    const resetPassword = async (token, password, passwordConfirmation) => {
        await api.Authentication.changePasswordRequest(token, password, passwordConfirmation);
        const isAuthenticated = await checkAuthentication();

        if(isAuthenticated === true){
            goToPrivateArea();
        }else {
            throw new Error("Impossibile caricare i dati dell'utente");
        }
    }

    // Create context data
    const context = {
        isAuthenticated,
        login,
        logout,
        resetPassword
    };

    return (
        <AuthenticationContext.Provider value={context} {...props}>
            <LoadingOverlay isLoading={isInitializing} size='large'>
                {props.children}
            </LoadingOverlay>
        </AuthenticationContext.Provider>
    )
};

const useAuthentication = () => {
    return React.useContext(AuthenticationContext);
};

const RequireAuthentication = (props) => {
    const {children} = props;
    const {isAuthenticated} = useAuthentication();
    let location = useLocation();

    if(isAuthenticated){
        // Return the children when authenticated
        return children;
    }else{
        // If not authenticated redirect to the /auth/login page and save the current
        // location they were trying to go to when they were redirected.
        // This allows us to send them to that page after they login.
        // The login page has to be the next location in the history, if a redirect happen
        // the state containing the from path cannot be read.
        return <Navigate to="/auth/login"  state={{ from: location }} replace/>;
    }
}

export {AuthenticationProvider, useAuthentication, RequireAuthentication}